// eslint-disable-next-line no-restricted-imports
import { Typography, type TypographyProps } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { type ReactElement } from "react";

export const TEXT_VARIANTS = ["h5", "h6", "body1", "body2", "caption", "overline"] as const;
export type TextVariant = (typeof TEXT_VARIANTS)[number];

/**
 * We restrict text to a fixed set of variants.
 */
export interface TextProps extends Omit<
  TypographyProps,
  "component" | "variant" | "fontWeight" | "fontSize" | "lineHeight"
> {
  variant?: TextVariant;
  light?: boolean;
  medium?: boolean;
  semibold?: boolean;
  bold?: boolean;
  italic?: boolean;
  inline?: boolean;
}

/**
 * WCAG 2.1 compliant Text component.
 * ---
 *
 * In order to meet WCAG 2.1 accessibility guidelines, this component
 * is by default a `p` element. There are rare cases where you may want
 * to use a `span` element (especially in some nested components) and you can do so by setting the `inline` prop.
 *
 * You can choose a number of variants, to achieve your designer's style.
 *
 * See: https://www.notion.so/BP-Title-and-Text-Document-Hierarchy-5931375db62f464a8169de791f82c9da
 */
export function Text(props: TextProps): ReactElement {
  const { bold, semibold, medium, italic, light, sx, inline = false, ...otherProps } = props;
  return (
    <Typography
      {...otherProps}
      component={inline ? "span" : "p"}
      sx={mergeSxProps(
        [
          {
            fontWeight: (theme) => {
              if (bold) {
                return theme.typography.fontWeightBold;
              }

              if (semibold) {
                return theme.typography.fontWeightSemibold;
              }

              if (medium) {
                return theme.typography.fontWeightMedium;
              }

              if (light) {
                return theme.typography.fontWeightLight;
              }

              return theme.typography.fontWeightRegular;
            },
          },
          { fontStyle: italic ? "italic" : "normal" },
        ],
        sx
      )}
    />
  );
}
